/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import type {
  Token,
  TokenDefinition,
} from '@/components/ui/TokenizedInput/types';
import { Calendar, CheckSquare, Filter, Hash, Tag, User } from 'lucide-react';

export interface FilterFunction<T = any> {
  (item: T, token: Token): boolean;
}

export interface FieldToTokenConfig {
  skipFields?: string[];
  customTokens?: TokenDefinition[];
  fetchOptionsMap?: Record<string, (search: string) => Promise<any[]>>;
}

// Default icons for common field types
export const defaultFieldIcons = {
  text: Tag,
  textarea: Tag,
  markdown: Tag,
  richtext: Tag,
  select: Hash,
  date: Calendar,
  boolean: CheckSquare,
  foreignKey: User,
  foreignKeyArray: User,
  ticketStatus: Hash,
  hidden: Tag,
  default: Filter,
} as const;

// Default transform functions by field type
export const defaultTransforms = {
  date: (value: any) =>
    value ? new Date(value).toISOString().split('T')[0] : null,
  boolean: (value: any) => Boolean(value),
  foreignKey: (value: any) => (value ? String(value) : null),
  foreignKeyArray: (value: any) =>
    Array.isArray(value) ? value.map(String) : [],
  default: (value: any) => value,
} as const;

// Get transform for field type
export function getDefaultTransform(fieldType?: string): (value: any) => any {
  if (!fieldType) return defaultTransforms.default;
  return (
    defaultTransforms[fieldType as keyof typeof defaultTransforms] ||
    defaultTransforms.default
  );
}

// Standard filter operations with null/undefined handling
export const filterOperations = {
  is: (value: any, tokenValue: any) => {
    // Handle null/undefined comparisons
    if (value == null && tokenValue === '') return true;
    if (value == null || tokenValue == null) return false;
    return String(value) === String(tokenValue);
  },
  is_not: (value: any, tokenValue: any) => {
    if (value == null && tokenValue === '') return false;
    if (value == null || tokenValue == null) return true;
    return String(value) !== String(tokenValue);
  },
  contains: (value: any, tokenValue: any) => {
    if (value == null) return false;
    return String(value)
      .toLowerCase()
      .includes(String(tokenValue).toLowerCase());
  },
  contains_not: (value: any, tokenValue: any) => {
    if (value == null) return true;
    return !String(value)
      .toLowerCase()
      .includes(String(tokenValue).toLowerCase());
  },
  gt: (value: any, tokenValue: any) => {
    if (value == null || tokenValue == null) return false;
    return value > tokenValue;
  },
  lt: (value: any, tokenValue: any) => {
    if (value == null || tokenValue == null) return false;
    return value < tokenValue;
  },
  gte: (value: any, tokenValue: any) => {
    if (value == null || tokenValue == null) return false;
    return value >= tokenValue;
  },
  lte: (value: any, tokenValue: any) => {
    if (value == null || tokenValue == null) return false;
    return value <= tokenValue;
  },
  in_array: (value: any[], tokenValue: any) => {
    if (!Array.isArray(value)) return false;
    return value.includes(tokenValue);
  },
  not_in_array: (value: any[], tokenValue: any) => {
    if (!Array.isArray(value)) return true;
    return !value.includes(tokenValue);
  },
};

// Get default operations for a token type
export function getDefaultOperationsForType(
  type:
    | 'text'
    | 'select'
    | 'date'
    | 'boolean'
    | 'foreignKey'
    | 'foreignKeyArray'
    | 'tags' // FIXME: temp for legacy lists
    | 'select'
    | 'richtext'
    | 'textarea',
): string[] {
  switch (type) {
    case 'text':
    case 'textarea':
    case 'richtext':
    case 'foreignKeyArray':
    case 'tags': // FIXME: temp for legacy lists
      return ['contains', 'contains_not', 'is', 'is_not'];
    case 'date':
      return ['is', 'is_not', 'gt', 'lt', 'gte', 'lte'];
    case 'boolean':
      return ['is'];
    case 'select':
    case 'foreignKey':
    default:
      return ['is', 'is_not'];
  }
}
